import { useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import Typography from '@material-ui/core/Typography';
import {
  DeployedUnit,
  FLEET_COMPONENTS_ANNOTATION,
  FLEET_SHAPE_ANNOTATION,
  parseImageTag,
  promotableUnits,
} from '@internal/backstage-plugin-release-common';
import { usePermission } from '@backstage/plugin-permission-react';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import PublishIcon from '@material-ui/icons/Publish';
import RestoreIcon from '@material-ui/icons/Restore';
import {
  releasePromotePermission,
  releaseRollbackPermission,
} from '@internal/backstage-plugin-release-common';
import { PendingPromotions, ReleaseLineSummary } from './PendingPromotions';
import { ReleaseAction, ReleaseDialog } from './ReleaseDialog';
import { useFleetDeployments } from './useFleetDeployments';
import { useReleaseStates } from './useReleaseStates';
import { useStartRelease } from './useStartRelease';


/** True for entities that actually deploy something. Excludes the
 * special-shaped ones — the API gateway and the seed orchestrator carry no
 * `values.yaml`, so there is nothing to show and nothing to promote. */
export const isDeploymentsCardAvailable = (entity: Entity): boolean => {
  const annotations = entity.metadata.annotations ?? {};
  if (annotations[FLEET_SHAPE_ANNOTATION] === 'special') {
    return false;
  }
  return Boolean(annotations[FLEET_COMPONENTS_ANNOTATION]);
};

interface Row {
  environment: string;
  component: string;
  version: string;
}


const columns: TableColumn<Row>[] = [
  // `api` / `ep` — the base chart's deployments. Deliberately not called
  // "component": in the catalog that word means the whole service, and this
  // card sits next to a fleet card that uses it that way.
  { title: 'Workload', field: 'component' },
  { title: 'Environment', field: 'environment' },
  { title: 'Version', field: 'version' },
];

/** The full overlay value minus its build-target prefix, so the column reads
 * `1.0.2-rc.2` rather than `api-1.0.2-rc.2` and lines up across components.
 * Falls back to the raw tag when it does not parse — a legacy shape is worth
 * seeing, not hiding. */
function versionOf(unit: DeployedUnit): string {
  return parseImageTag(unit.imageTag)?.version ?? unit.imageTag;
}

/** MUI pins a CardHeader's action to the top and pulls it 8px above the
 * header's own padding, which is right for an icon button and wrong for a pair
 * of buttons that should read as sitting beside the title. */
const useStyles = makeStyles({
  headerAction: { alignSelf: 'center', marginTop: 0, marginRight: 0 },
});

export const EntityDeploymentsCard = () => {
  const { entity } = useEntity();
  const classes = useStyles();
  const state = useFleetDeployments({ service: entity.metadata.name });
  const [action, setAction] = useState<ReleaseAction | null>(null);
  const onConfirm = useStartRelease(() => setAction(null));
  // What is in flight: the open line and the gitops pull requests that have
  // not merged, which the deployed versions below cannot show.
  const releases = useReleaseStates([entity.metadata.name], state.generation);
  const inFlight =
    releases.kind === 'loaded'
      ? releases.byService[entity.metadata.name]
      : undefined;

  // Courtesy, not control — the gate is on the scaffolder action. The base
  // permissions are enough to decide whether to offer the button at all; which
  // environment it lands in, and the permission that needs, is the dialog's.
  const canPromote = usePermission({
    permission: releasePromotePermission,
  }).allowed;
  const canRollback = usePermission({
    permission: releaseRollbackPermission,
  }).allowed;

  if (state.kind === 'loading') {
    return (
      <InfoCard title="Deployments">
        <Progress />
      </InfoCard>
    );
  }

  if (state.kind === 'error') {
    return (
      <InfoCard title="Deployments">
        <ResponseErrorPanel error={state.error} />
      </InfoCard>
    );
  }

  const service = state.response.services.find(
    entry => entry.service === entity.metadata.name,
  );

  if (!service) {
    return (
      <InfoCard title="Deployments">
        <Typography variant="body2" color="textSecondary">
          No deployments found for this component in gitops-platform.
        </Typography>
      </InfoCard>
    );
  }

  // Grouped by workload, because that is the first column now: leaving the
  // backend's environment-first order would make it read api, ep, api, ep.
  const rows: Row[] = promotableUnits(service)
    .map(unit => ({
      environment: unit.environment,
      component: unit.component,
      version: versionOf(unit),
    }))
    .sort(
      (a, b) =>
        a.component.localeCompare(b.component) ||
        a.environment.localeCompare(b.environment),
    );


  const start = (next: ReleaseAction, allowed: boolean) => {
    const label = next === 'promote' ? 'Promote' : 'Rollback';
    return (
      // Icon only, so the label lives in the tooltip — this card is a summary
      // beside About, and two labelled buttons weighed more than the rows they
      // sit above. The Releases tab keeps its labels: that is where someone
      // goes to release, and there the words are the point.
      <Tooltip
        title={allowed ? label : `You cannot ${label.toLowerCase()} this.`}
      >
        <span>
          <IconButton
            aria-label={label}
            color={next === 'promote' ? 'primary' : 'default'}
            disabled={!allowed}
            onClick={() => setAction(next)}
          >
            {next === 'promote' ? <PublishIcon /> : <RestoreIcon />}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <InfoCard
      title="Deployments"
      // The table draws its own header rule right below, so the card's divider
      // put two lines a few pixels apart. Without it the header's own bottom
      // padding stacked on the content's top padding and left the title
      // floating well above the rows.
      divider={false}
      headerStyle={{ paddingBottom: 0 }}
      // A step down from the catalog cards' default: this one sits above a
      // dense table rather than a block of fields, and the heading was
      // carrying more weight than the rows under it.
      titleTypographyProps={{ variant: 'h6' }}
      headerProps={{ classes: { action: classes.headerAction } }}
      action={
        <Box display="flex" gridGap={8}>
          {start('promote', canPromote)}
          {start('rollback', canRollback)}
        </Box>
      }
    >
      <Table
        options={{
          search: false,
          paging: false,
          toolbar: false,
          padding: 'dense',
        }}
        columns={columns}
        data={rows}
      />


      {inFlight?.line && (
        <Box pt={1}>
          <Typography variant="body2" color="textSecondary" component="div">
            Release line <ReleaseLineSummary line={inFlight.line} />
          </Typography>
        </Box>
      )}
      <PendingPromotions pending={inFlight?.pending ?? []} />

      {service.rebuildsPerEnvironment && (
        <Typography variant="body2" color="textSecondary">
          This component rebuilds its image per environment instead of
          retagging, so production does not run the artifact staging validated.
        </Typography>
      )}
      {/* The versions are here; the values behind them are a tab away. A
          relative link, so it lands on this entity's tab wherever the catalog
          is mounted. */}
      <Box pt={1}>
        <Typography variant="body2">
          <Link to="configuration">Configuration across environments</Link>
        </Typography>
      </Box>
      <ReleaseDialog
        action={action}
        services={[service]}
        onClose={() => setAction(null)}
        onConfirm={onConfirm}
      />
    </InfoCard>
  );
};
